import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Plan theme (Super Admin → Subscriptions → Edit Plan → Theme), received with
 * the organisation's plan at login. `colours` are final per-component colours
 * (only those that differ from ESG Default); `modules` hold a module's own
 * colours, used while the user is inside that module; `builtInModules` are
 * modules with their own built-in theme (e.g. dMRV) that keep it unless the
 * plan says otherwise.
 */
export interface PlanThemePayload {
  colours: Record<string, string>;
  modules?: { path: string; colours: Record<string, string> }[];
  /** `useDefault`: the module's own theme is switched off ("ESG Default theme") and it looks like the rest of the platform. */
  builtInModules?: { key: string; paths: string[]; cssScope: string; applyPlanTheme: boolean; useDefault?: boolean; colourMap?: Partial<ModuleColourMap> }[];
}

const STYLE_ID = 'esg-plan-theme';
const ATTR = 'data-plan-theme';
const BOOST = ':not(#esg-plan-theme-boost)';
const HEX = /^#[0-9a-f]{6}$/i;

const toRgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const mix = (hex: string, amount: number) => {
  const rgb = toRgb(hex).map((c) => (amount >= 0 ? c + (255 - c) * amount : c * (1 + amount)));
  return `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
};
/** #RRGGBB → "h s% l%" (the format of the platform's colour variables). */
const hsl = (hex: string) => {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const luminance = (hex: string) => {
  const [r, g, b] = toRgb(hex).map((v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const DEFAULT_TEXT_LUMINANCE = luminance('#0F172A');
/**
 * Surfaces that carry the platform's default (dark) text. Text is never
 * recoloured, so a colour too dark to read on is lightened just enough.
 */
const TEXT_SURFACES = new Set([
  'pageBackground', 'cardBackground', 'leftMenuBackground', 'topBarBackground', 'popupBackground', 'loginBackground',
  'menuSelectedItem', 'menuHoverItem', 'otherButtons', 'tabs', 'dropdownBox', 'dropdownList',
  'dropdownSelectedOption', 'dropdownHoverOption', 'textBoxes', 'tableHeader', 'tableRowHover', 'lists', 'tagsLabels',
]);
const readable = (hex: string) => {
  let out = hex;
  for (let i = 0; i < 12 && (luminance(out) + 0.05) / (DEFAULT_TEXT_LUMINANCE + 0.05) < 4.5; i++) out = mix(out, 0.2);
  return out;
};

const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Wording on a themed surface is pure black or pure white, never a tint of
 * one: a near-white cream reads as a smudge on a strong colour, and whichever
 * of the two ends wins on contrast always beats anything between them.
 */
const inkOn = (bg: string) => (contrast('#FFFFFF', bg) >= contrast('#000000', bg) ? '#FFFFFF' : '#000000');

/**
 * An icon keeps the plan's own icon colour, but only while it can be seen on
 * the surface it sits on — the plan's grey on a deep teal tile is a glyph you
 * have to hunt for. The colour is walked lighter, then darker, keeping the
 * plan's hue, and the first direction to clear 3:1 (the WCAG floor for a
 * graphic, as opposed to 4.5:1 for text) wins. Only when neither direction
 * gets there does it give up the hue and fall back to black or white.
 */
const visibleOn = (hex: string, bg: string, min = 3) => {
  if (contrast(hex, bg) >= min) return hex;
  for (const step of [0.18, -0.18]) {
    let out = hex;
    for (let i = 0; i < 12; i++) {
      out = mix(out, step);
      if (contrast(out, bg) >= min) return out;
    }
  }
  return inkOn(bg);
};

/**
 * Builds the theme stylesheet: colour variables + surface rules. Normal text is never recoloured; blue accent text
 * follows the main colour. Plan colours are light-surface palettes, so every rule is limited to light mode
 * (`html:not(.dark)`); dark mode keeps the platform's own dark palette.
 */
export function buildPlanThemeCss(colours: Record<string, string>, keepScopes: string[], recolourScopes: string[], contentScope: string | null = null): string {
  const c = (k: string) => (HEX.test(colours[k] ?? '') ? (TEXT_SURFACES.has(k) ? readable(colours[k]) : colours[k]) : null);
  const vars: string[] = [];
  const v = (name: string, hex: string | null) => { if (hex) vars.push(`${name}: ${hsl(hex)};`); };
  v('--background', c('pageBackground')); v('--background-secondary', c('pageBackground'));
  v('--card', c('cardBackground')); v('--surface', c('cardBackground'));
  v('--popover', c('dropdownList'));
  const primary = c('mainButtons') ?? c('brandColour');
  // The ink on a coloured surface follows the surface: a plan that picks a pale
  // main colour otherwise keeps the stock white `--primary-foreground` and loses
  // every white-on-primary icon and label in the modules.
  if (primary) { v('--primary', primary); v('--primary-light', mix(primary, 0.25)); v('--primary-dark', mix(primary, -0.15)); v('--blue', primary); v('--primary-foreground', inkOn(primary)); }
  const secondary = c('otherButtons');
  v('--secondary', secondary);
  if (secondary) v('--secondary-foreground', inkOn(secondary));
  v('--destructive', c('deleteButtons')); v('--danger', c('deleteButtons')); v('--red', c('deleteButtons'));
  v('--ring', c('fieldOutline'));
  v('--border', c('linesBorders')); v('--border-light', c('linesBorders')); v('--input', c('linesBorders'));
  v('--success', c('statusColours')); v('--green', c('statusColours'));
  v('--muted', c('tabs'));
  const brand = c('brandColour') ?? primary;
  if (brand) vars.push(`--gradient-primary: linear-gradient(135deg, ${brand} 0%, ${mix(brand, 0.3)} 100%);`);

  // Surfaces inside a module that keeps its own built-in theme are left alone.
  const ex = keepScopes.length ? `:not(:is(${keepScopes.map((s) => `${s}, ${s} *`).join(', ')}))` : '';
  const rules: string[] = [];
  // For a module-scoped colour, every surface rule is confined to that module's
  // own content scope (e.g. dMRV's `.dmrv-theme`, which its pages AND its
  // portalled pop-ups both carry) — matched as that element itself or any
  // descendant of it — so nothing outside the module is ever painted. Plan-wide
  // colours (contentScope null) stay global, exactly as before.
  const within = (s: string) => (contentScope ? `:is(${contentScope} ${s}${ex}, ${contentScope}${s}${ex})` : `${s}${ex}`);
  const rule = (selectors: string[], decl: string, hex: string | null) => {
    if (!hex) return;
    // `:not(#…)` lifts the rule above every class-based !important rule the modules force (blue tabs / buttons).
    rules.push(`${selectors.map((s) => `html[${ATTR}]:not(.dark)${BOOST} ${within(s)}`).join(',\n')} { ${decl.replace(/\$/g, hex)} }`);
  };
  const bg = 'background-color: $ !important;';
  // The shell chrome (page background, the whole sidebar that lists every
  // module, the top bar and its menu items) is painted ONLY by plan-wide
  // colours. A module-scoped colour (contentScope set) never touches it, so the
  // chrome stays the ESG Default look no matter which module you open — only
  // that module's own pages and pop-ups follow the colour (scoped above).
  if (!contentScope) {
    rule(['[data-theme-part="page"]'], bg, c('pageBackground'));
    rule(['[data-theme-part="left-menu"]'], bg, c('leftMenuBackground'));
    rule(['[data-theme-part="top-bar"]'], bg, c('topBarBackground'));
    rule(['[data-theme-part="left-menu"] .bg-blue-50'], bg, c('menuSelectedItem'));
    // The selected dMRV item (dark green pill, light text) keeps its own colour on hover.
    const dmrvSelected = '.bg-\\[\\#2c5a45\\]';
    rule([`[data-theme-part="left-menu"] a:hover:not(.bg-blue-50):not(${dmrvSelected})`, `[data-theme-part="left-menu"] button:hover:not(.bg-blue-50):not(${dmrvSelected})`], bg, c('menuHoverItem'));
    rule(['[data-theme-part="left-menu"] .bg-blue-500'], bg, brand);
  }
  rule(['[role="dialog"]', '[role="alertdialog"]'], bg, c('popupBackground'));
  // Hard-coded blue / indigo surfaces across the modules follow the plan's main colour:
  // strong shades → the colour, light shades → tints, gradients → colour → slightly darker.
  if (primary) {
    const esc = (cls: string) => cls.replace(':', '\\:');
    const solid = [500, 600, 700].flatMap((n) => [`.bg-blue-${n}`, `.bg-indigo-${n}`, `.${esc(`hover:bg-blue-${n}`)}:hover`, `.${esc(`hover:bg-indigo-${n}`)}:hover`]);
    // Also the platform's forced primary buttons (.btn-primary, button/a.bg-blue-* incl. hover).
    rule([...solid, '.btn-primary', '.btn-primary:hover', 'button.bg-blue-500:hover', 'button.bg-blue-600:hover', 'a.bg-blue-500:hover', 'a.bg-blue-600:hover'], bg, primary);
    rule(['.icon-container-blue'], `${bg} border-color: ${mix(primary, 0.7)} !important;`, mix(primary, 0.9));
    const tints: [number, number][] = [[50, 0.92], [100, 0.85], [200, 0.72]];
    for (const [n, amt] of tints) {
      rule([`.bg-blue-${n}`, `.bg-indigo-${n}`, `.${esc(`hover:bg-blue-${n}`)}:hover`, `.${esc(`hover:bg-indigo-${n}`)}:hover`], bg, mix(primary, amt));
      rule([`.border-blue-${n}`, `.border-indigo-${n}`], 'border-color: $ !important;', mix(primary, amt - 0.1));
    }
    rule([300, 400, 500, 600, 700].flatMap((n) => [`.border-blue-${n}`, `.border-indigo-${n}`, `.${esc(`hover:border-blue-${n}`)}:hover`]), 'border-color: $ !important;', primary);
    rule([400, 500, 600].flatMap((n) => [`.ring-blue-${n}`, `.${esc(`focus:ring-blue-${n}`)}:focus`, `.${esc(`focus-visible:ring-blue-${n}`)}:focus-visible`]), '--tw-ring-color: $ !important;', primary);
    rule([400, 500, 600].map((n) => `.shadow-blue-${n}`), '--tw-shadow-color: $40 !important; --tw-shadow: var(--tw-shadow-colored) !important;', primary);
    rule([500, 600, 700].flatMap((n) => [`.from-blue-${n}`, `.from-indigo-${n}`]), '--tw-gradient-from: $ var(--tw-gradient-from-position) !important;', primary);
    rule([500, 600, 700, 800].flatMap((n) => [`.to-blue-${n}`, `.to-indigo-${n}`, `.via-blue-${n}`, `.via-indigo-${n}`]), '--tw-gradient-to: $ var(--tw-gradient-to-position) !important;', mix(primary, -0.12));
    rule(['.from-blue-50', '.from-indigo-50'], '--tw-gradient-from: $ var(--tw-gradient-from-position) !important;', mix(primary, 0.92));
    rule(['.to-blue-50', '.to-blue-100', '.to-indigo-50', '.to-indigo-100'], '--tw-gradient-to: $ var(--tw-gradient-to-position) !important;', mix(primary, 0.85));
    // Blue / indigo ACCENT text (selected menu item, links, highlighted labels and icons) follows the main colour,
    // darkened just enough to stay readable on light surfaces. Normal (dark) text is never changed.
    // What sits ON those strong surfaces. The surface gets repainted the plan's
    // colour while the markup underneath still says `text-white`, so a plan
    // that picks a pale colour leaves white lettering on a pale tile. Text
    // goes to black or white by contrast; icons keep the plan's icon hue if
    // it can be seen there, and are walked until it can if not.
    const strong = [
      ...[500, 600, 700].flatMap((n) => [`.bg-blue-${n}`, `.bg-indigo-${n}`, `.from-blue-${n}`, `.from-indigo-${n}`]),
      '.btn-primary',
    ];
    rule(strong, 'color: $ !important;', inkOn(primary));
    rule(strong.map((sel) => `${sel} svg.lucide`), 'color: $ !important;', visibleOn(c('icons') ?? primary, primary));

    let accent = primary;
    for (let i = 0; i < 12 && 1.05 / (luminance(accent) + 0.05) < 4.5; i++) accent = mix(accent, -0.12);
    const accentText = [400, 500, 600, 700, 800].flatMap((n) => [`.text-blue-${n}`, `.text-indigo-${n}`, `.${esc(`hover:text-blue-${n}`)}:hover`, `.${esc(`hover:text-indigo-${n}`)}:hover`, `.group:hover .${esc(`group-hover:text-blue-${n}`)}`]);
    rule(accentText, 'color: $ !important;', accent);
  }
  rule(['[role="tablist"]'], bg, c('tabs'));
  // The platform forces active tabs to a blue gradient (background shorthand, !important) with white text,
  // so the selected tab gets the full colour as a shorthand too — replacing the gradient image.
  // The forced active-tab text is white: a colour too light for white text is darkened just enough.
  const forWhiteText = (hex: string | null) => {
    if (!hex) return hex;
    let out = hex;
    for (let i = 0; i < 12 && 1.05 / (luminance(out) + 0.05) < 3; i++) out = mix(out, -0.15);
    return out;
  };
  rule(['[role="tab"][data-state="active"]', '.tabs-trigger[data-state="active"]'], 'background: $ !important; border-color: $ !important;', forWhiteText(c('selectedTab') ?? primary));
  rule(['button[role="combobox"]'], bg, c('dropdownBox'));
  rule(['[role="listbox"]', '[role="menu"]'], bg, c('dropdownList'));
  // Hovered / selected options are light surfaces, so they keep the normal dark text (the options' own hover style
  // uses white accent text). The selected colour wins when an option is both selected and hovered.
  const optionText = ' color: hsl(var(--foreground)) !important;';
  rule(['[role="option"][data-highlighted]', '[role="menuitem"][data-highlighted]'], bg + optionText, c('dropdownHoverOption'));
  rule(['[role="option"][data-state="checked"]', '[role="option"][aria-selected="true"]'], bg + optionText, c('dropdownSelectedOption'));
  rule(['input:not([type="checkbox"]):not([type="radio"]):not([type="color"]):not([type="range"])', 'textarea'], bg, c('textBoxes'));
  rule(['[role="checkbox"][data-state="checked"]', '[role="switch"][data-state="checked"]', '[role="radio"][data-state="checked"]'], `${bg} border-color: $ !important;`, c('tickBoxes'));
  rule(['input[type="checkbox"]', 'input[type="radio"]'], 'accent-color: $;', c('tickBoxes'));
  rule(['thead', 'thead tr', 'thead th'], bg, c('tableHeader'));
  rule(['tbody tr:hover'], bg, c('tableRowHover'));
  rule(['[role="listitem"]'], bg, c('lists'));
  rule(['[role="progressbar"] > *'], bg, c('progressBars'));
  rule(['[role="tooltip"]'], bg, c('hoverHints'));
  rule(['button:has(> svg.lucide:only-child)'], 'color: $;', c('iconButtons'));
  // The plan's icon colour is for icons on ordinary surfaces. One that is
  // given a colour outright — white on a coloured tile, or a hard-coded hex —
  // already matches its background, and overriding it is what turned the
  // building glyph on the profile tile into grey-on-teal.
  const explicit = ':is([class*="text-white"], [class*="text-black"], [class*="text-["])';
  if (c('icons')) rules.push(`html[${ATTR}]:not(.dark) ${within(`:where(svg.lucide):not(${explicit}):not(${explicit} *)`)} { color: ${c('icons')}; }`);
  if (c('scrollBar')) {
    const sbPrefix = contentScope ? `${contentScope} ` : '';
    const sbStar = contentScope ? `:is(${contentScope}, ${contentScope} *)` : '*';
    rules.push(`html[${ATTR}]:not(.dark) ${sbPrefix}::-webkit-scrollbar-thumb { background-color: ${c('scrollBar')} !important; }`, `html[${ATTR}]:not(.dark) ${sbStar} { scrollbar-color: ${c('scrollBar')} transparent; }`);
  }

  const varBlock = vars.join(' ');
  // Plan-wide colours set the variables on :root so the whole app follows them.
  // Module-scoped colours skip :root and reach the module through its own scope
  // below (recolourScopes), so the variables colour the module's pages and its
  // pop-ups — which carry the module's scope class — but never the shell chrome.
  const css = contentScope ? [] : [`:root[${ATTR}]:not(.dark) { ${varBlock} }`];
  // Modules whose plan theme also recolours their own built-in theme.
  for (const s of recolourScopes) css.push(`html[${ATTR}]:not(.dark) ${s}${s}${s} { ${varBlock} }`);
  return [...css, ...rules].join('\n');
}

// ── Built-in module switched to the platform look ("ESG Default theme") ─────
// The module's own stylesheet is switched off through `data-module-default` on <html>. Colours it hard-codes
// (Tailwind `bg-[#…]` classes, inline styles, SVG fills) are found in the page as it renders and converted:
// greens / limes → the platform's main colour (ESG Default blue unless the plan sets one), greyish tones →
// slate, warm warning / error tones and blues → unchanged.
const MODULE_DEFAULT_ATTR = 'data-module-default';
const MODULE_DEFAULT_STYLE_ID = 'esg-module-default';
const ESG_DEFAULT_PRIMARY = '#2563EB';

const toHslParts = (hex: string): [number, number, number] => {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4) * 60;
  return [h, s, l];
};
const fromHslParts = (h: number, s: number, l: number) => {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${[f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('')}`;
};
/** One colour of a module's own palette → the platform look. */
/**
 * `map` is the module's own colours translated into this plan's, built from the
 * module's component mapping — its forest green is the open menu row, so it
 * becomes whatever this plan uses there. Only the map for the role being
 * painted is consulted, never another role's: a plan that says nothing about
 * card backgrounds but does set the text on buttons would otherwise lend that
 * answer to dMRV's white, and every white card, popup and input in the module
 * would come out the colour of button lettering. A role with no entry for the
 * colour means the plan has no opinion on it, and the rules below take over —
 * they keep white white and otherwise shift the colour towards the plan.
 */
const toPlatformColour = (hex: string, primary: string, map?: Partial<ModuleColourMap>, role: ColourRole = 'bg') => {
  const mapped = map?.[role]?.[hex.toUpperCase()];
  if (mapped) return mapped;
  const [h, s, l] = toHslParts(hex);
  if (s < 0.3) return fromHslParts(215, l > 0.9 ? 0.35 : 0.2, l); // neutrals → slate (same lightness)
  if (h < 50 || h >= 190) return hex; // amber / red / terracotta (status) and blue / violet keep their meaning
  const pl = toHslParts(primary)[2];
  if (l < 0.22) return mix(primary, -0.45); // very dark green → dark main colour
  if (l <= pl + 0.05) return primary; // brand green → main colour
  return mix(primary, Math.min(0.95, (l - pl) / (1 - pl))); // lighter greens / limes → tints
};
const cssColourToHex = (v: string): { hex: string; alpha: number } | null => {
  const t = v.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(t)) return { hex: t, alpha: 1 };
  const m = t.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+%?))?\s*\)$/);
  if (!m) return null;
  const alpha = m[4] == null ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  return { hex: `#${[m[1], m[2], m[3]].map((x) => Number(x).toString(16).padStart(2, '0')).join('')}`, alpha };
};
const withAlpha = (hex: string, alpha: number) => (alpha >= 1 ? hex : `rgba(${toRgb(hex).join(', ')}, ${alpha})`);

/**
 * The module's own colours translated into this plan's, keyed by the CSS role
 * each one plays (see the server's `buildColourMap`). dMRV paints eleven
 * different things `#FFFFFF`, from cards to the label on a Save button, so the
 * hex alone cannot say what a colour should become — the role does.
 */
type ModuleColourMap = { bg: Record<string, string>; fg: Record<string, string>; border: Record<string, string> };
type ColourRole = keyof ModuleColourMap;

/** Foreground utilities paint text and icon glyphs; the rest outline or fill. */
const FG_UTILITIES = new Set(['text', 'fill', 'stroke', 'placeholder']);
const BG_UTILITIES = new Set(['bg', 'accent']);
const utilityRole = (utility: string): ColourRole =>
  FG_UTILITIES.has(utility) ? 'fg' : BG_UTILITIES.has(utility) ? 'bg' : 'border';
const propertyRole = (property: string): ColourRole =>
  property === 'color' || property === 'fill' || property === 'stroke' ? 'fg'
    : property === 'background' || property === 'background-color' ? 'bg'
    : 'border';

const UTILITY = /^((?:[a-z-]+:)*)(bg|text|border|border-[trblxy]|divide|ring|accent|outline|fill|stroke|placeholder)-\[#([0-9a-f]{6})\](?:\/(\d{1,3}))?$/i;
const VARIANT: Record<string, { pre?: string; post?: string }> = {
  hover: { post: ':hover' }, focus: { post: ':focus' }, 'focus-visible': { post: ':focus-visible' }, 'focus-within': { post: ':focus-within' },
  active: { post: ':active' }, disabled: { post: ':disabled' }, 'group-hover': { pre: '.group:hover ' },
  before: { post: '::before' }, after: { post: '::after' }, placeholder: { post: '::placeholder' },
};
const PROPERTY: Record<string, string[]> = {
  bg: ['background-color'], text: ['color'], border: ['border-color'], divide: ['border-color'],
  'border-t': ['border-top-color'], 'border-b': ['border-bottom-color'], 'border-l': ['border-left-color'], 'border-r': ['border-right-color'],
  'border-x': ['border-left-color', 'border-right-color'], 'border-y': ['border-top-color', 'border-bottom-color'],
  ring: ['--tw-ring-color'], accent: ['accent-color'], outline: ['outline-color'], fill: ['fill'], stroke: ['stroke'], placeholder: ['color'],
};
const INLINE = /(?:^|;)\s*((?:background-color|background|color|border-color|border-(?:top|bottom|left|right)-color|fill|stroke|outline-color)\s*:\s*(#[0-9a-fA-F]{6}|rgba?\([^)]*\)))/g;

/** CSS rules converting the colours the module hard-codes, for the classes / styles / fills seen so far. */
function buildModuleDefaultCss(scopes: string[], classes: Set<string>, inline: Set<string>, svg: Set<string>, primary: string, map?: Partial<ModuleColourMap>): string {
  const scope = `:is(${scopes.join(', ')})`;
  const head = `html[${MODULE_DEFAULT_ATTR}]:not(.dark)${BOOST}`;
  const rules: string[] = [];
  for (const cls of classes) {
    const m = cls.match(UTILITY);
    if (!m) continue;
    const variants = m[1].split(':').filter(Boolean);
    let pre = '', post = '', ok = true;
    for (const v of variants) { const x = VARIANT[v]; if (!x) { ok = false; break; } pre += x.pre ?? ''; post += x.post ?? ''; }
    if (!ok) continue;
    const props = PROPERTY[m[2].toLowerCase()];
    if (!props) continue;
    const colour = withAlpha(toPlatformColour(`#${m[3].toLowerCase()}`, primary, map, utilityRole(m[2].toLowerCase())), m[4] ? Number(m[4]) / 100 : 1);
    const el = `.${CSS.escape(cls)}`;
    const target = m[2] === 'divide' ? `${el} > :not([hidden]) ~ :not([hidden])` : el;
    const decl = props.map((p) => `${p}: ${colour} !important;`).join(' ');
    rules.push(`${head} ${scope} ${pre}${target}${post}, ${head} ${pre}${scope}${target}${post} { ${decl} }`);
  }
  for (const raw of inline) {
    const [prop, ...rest] = raw.split(':');
    const parsed = cssColourToHex(rest.join(':'));
    if (!parsed) continue;
    const name = prop.trim().toLowerCase();
    const colour = withAlpha(toPlatformColour(parsed.hex, primary, map, propertyRole(name)), parsed.alpha);
    const q = raw.replace(/"/g, '\\"');
    // `color:` must not match inside `background-color:` / `border-color:`.
    const sel = name === 'color' ? [`[style^="${q}"]`, `[style*="; ${q}"]`, `[style*=";${q}"]`] : [`[style*="${q}"]`];
    const p = name === 'background' ? 'background-color' : name;
    rules.push(`${sel.map((x) => `${head} ${scope} ${x}, ${head} ${scope}${x}`).join(', ')} { ${p}: ${colour} !important; }`);
  }
  for (const key of svg) {
    const [attr, hex] = key.split('=');
    rules.push(`${head} ${scope} [${attr}="${hex}" i] { ${attr}: ${toPlatformColour(hex.toLowerCase(), primary, map, 'fg')} !important; }`);
  }
  // Named Tailwind green / emerald / lime / teal utilities (e.g. `text-emerald-700`,
  // `bg-emerald-50`) are NOT `[#hex]`, so the scan above never saw them and they
  // stayed green inside a module switched to the platform look — the "mix" of the
  // plan's colour and leftover green. These shades are fixed, so convert them
  // statically the same way (strong greens → the main colour, light ones → tints).
  const GREENS: Record<string, Record<number, string>> = {
    emerald: { 50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b' },
    green: { 50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d' },
    lime: { 50: '#f7fee7', 100: '#ecfccb', 200: '#d9f99d', 300: '#bef264', 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#3f6212', 900: '#365314' },
    teal: { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  };
  const NAMED: [string, string, ColourRole][] = [['bg', 'background-color', 'bg'], ['text', 'color', 'fg'], ['border', 'border-color', 'border']];
  for (const [family, shades] of Object.entries(GREENS)) {
    for (const [shade, hex] of Object.entries(shades)) {
      for (const [util, prop, role] of NAMED) {
        const colour = toPlatformColour(hex, primary, map, role);
        const el = `.${util}-${family}-${shade}`;
        rules.push(`${head} ${scope} ${el}, ${head} ${scope}${el} { ${prop}: ${colour} !important; }`);
      }
    }
  }
  return rules.join('\n');
}

/**
 * Applies the plan theme to the whole tenant platform; inside a module that
 * has its own colours in the plan, that module's colours are used. Nothing is
 * applied when the plan uses the ESG Default theme.
 */
export default function PlanThemeApplier() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const theme = (user as { planTheme?: PlanThemePayload | null } | null)?.planTheme ?? null;

  useEffect(() => {
    const root = document.documentElement;
    const remove = () => { document.getElementById(STYLE_ID)?.remove(); root.removeAttribute(ATTR); };
    if (!theme || typeof theme !== 'object') { remove(); return; }

    const path = pathname.toLowerCase();
    const module = (theme.modules ?? []).find((m) => path === m.path || path.startsWith(`${m.path}/`));
    const colours = module?.colours ?? theme.colours ?? {};
    if (!Object.keys(colours).length) { remove(); return; }

    const builtIn = theme.builtInModules ?? [];
    // A module given its own colours in this plan is recoloured even if it has a built-in theme.
    const inModule = (b: { paths: string[] }) => b.paths.some((p) => path === p || path.startsWith(`${p}/`));
    const recolour = builtIn.filter((b) => b.applyPlanTheme || (module && inModule(b))).map((b) => b.cssScope);
    const keep = builtIn.filter((b) => !recolour.includes(b.cssScope)).map((b) => b.cssScope);

    // Colours that belong to the module currently open (per-module colours,
    // not plan-wide) and that module has its own scope class (e.g. dMRV's
    // `.dmrv-theme`): confine the whole theme to that class, so only the
    // module's own pages and pop-ups are painted and the shell chrome stays the
    // ESG Default look whatever module you open. Plan-wide colours (no `module`,
    // or a module without a scope class) keep painting the whole app.
    const contentScope = module ? (builtIn.find((b) => inModule(b))?.cssScope ?? null) : null;

    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) { style = document.createElement('style'); style.id = STYLE_ID; }
    style.textContent = buildPlanThemeCss(colours, keep, recolour, contentScope);
    document.head.appendChild(style); // always last, so it follows every module's own stylesheet
    root.setAttribute(ATTR, module ? `module:${module.path}` : 'plan');
  }, [theme, pathname]);

  // Built-in modules switched to the platform look: turn their own theme off and convert hard-coded colours.
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => { document.getElementById(MODULE_DEFAULT_STYLE_ID)?.remove(); root.removeAttribute(MODULE_DEFAULT_ATTR); };
    // A built-in module that has its OWN colours in this plan (e.g. the dMRV
    // palette applied via "Use these colours for dMRV") is themed by the plan
    // engine and its own stylesheet, which follow that colour. It must NOT also
    // go through the module-default converter — whose job is the opposite (strip
    // the module's theme for the ESG Default look) and which washes a muted
    // colour like the dark-forest hero out to slate. Only modules switched to
    // the platform look with NO colours of their own ("ESG Default theme") are
    // converted here.
    const themedModulePaths = new Set((((theme && typeof theme === 'object' ? theme.modules : null) ?? []) as { path?: string; colours?: Record<string, string> }[])
      .filter((m) => m && Object.keys(m.colours ?? {}).length > 0).map((m) => (m.path ?? '').toLowerCase()));
    const plain = (theme && typeof theme === 'object' ? theme.builtInModules ?? [] : [])
      .filter((b) => b.useDefault && !b.paths.some((p) => themedModulePaths.has(p.toLowerCase())));
    if (!plain.length) { clear(); return; }
    root.setAttribute(MODULE_DEFAULT_ATTR, plain.map((b) => b.key).join(' '));

    const path = pathname.toLowerCase();
    const module = (theme!.modules ?? []).find((m) => path === m.path || path.startsWith(`${m.path}/`));
    const colours = module?.colours ?? theme!.colours ?? {};
    const primary = [colours.mainButtons, colours.brandColour].find((h) => HEX.test(h ?? '')) ?? ESG_DEFAULT_PRIMARY;
    const scopes = plain.map((b) => b.cssScope);
    // When the open module has its OWN colours in this plan (e.g. the dMRV
    // palette applied via "Use these colours for dMRV"), its colour IS the
    // intent — so the converter maps the module's hard-coded hues onto that
    // colour (`primary`, by hue), NOT onto the per-component translation table.
    // That table is built from the PLAN-wide colours, which are empty here, so
    // it would map the module's greens to the ESG Default slate/blue and wipe
    // out the colour just applied. Only when the module has no colours of its
    // own ("ESG Default theme") do we use the table, which then correctly points
    // the module at the platform look.
    const moduleHasOwnColours = !!module && Object.keys(module.colours ?? {}).length > 0;
    const colourMap: ModuleColourMap = moduleHasOwnColours
      ? { bg: {}, fg: {}, border: {} }
      : {
          bg: Object.assign({}, ...plain.map((b) => b.colourMap?.bg ?? {})),
          fg: Object.assign({}, ...plain.map((b) => b.colourMap?.fg ?? {})),
          border: Object.assign({}, ...plain.map((b) => b.colourMap?.border ?? {})),
        };
    const within = scopes.flatMap((s) => [s, `${s} *`]).join(', ');
    const classes = new Set<string>(), inline = new Set<string>(), svg = new Set<string>();

    let style = document.getElementById(MODULE_DEFAULT_STYLE_ID) as HTMLStyleElement | null;
    if (!style) { style = document.createElement('style'); style.id = MODULE_DEFAULT_STYLE_ID; }
    const render = () => { style!.textContent = buildModuleDefaultCss(scopes, classes, inline, svg, primary, colourMap); document.head.appendChild(style!); };
    const scan = () => {
      let added = false;
      for (const el of Array.from(document.querySelectorAll(within))) {
        const cls = el.getAttribute('class');
        if (cls && cls.includes('[#')) for (const c of cls.split(/\s+/)) if (c.includes('[#') && !classes.has(c)) { classes.add(c); added = true; }
        const st = el.getAttribute('style');
        if (st) for (const m of st.matchAll(INLINE)) if (!inline.has(m[1])) { inline.add(m[1]); added = true; }
        for (const attr of ['fill', 'stroke']) {
          const v = el.getAttribute(attr);
          if (v && /^#[0-9a-f]{6}$/i.test(v)) { const k = `${attr}=${v.toLowerCase()}`; if (!svg.has(k)) { svg.add(k); added = true; } }
        }
      }
      if (added) render();
    };
    render();
    scan();
    let frame = 0;
    const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; scan(); }); });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'fill', 'stroke'] });
    return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [theme, pathname]);

  useEffect(() => () => {
    document.getElementById(STYLE_ID)?.remove(); document.documentElement.removeAttribute(ATTR);
    document.getElementById(MODULE_DEFAULT_STYLE_ID)?.remove(); document.documentElement.removeAttribute(MODULE_DEFAULT_ATTR);
  }, []);

  return null;
}
